import React, { useMemo, useState } from "react";
import { useIsReadOnlyManager } from "@/shared/readOnlyManager";
import { connect } from "react-redux";
import { Container } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";

import SelectProgram from "../components/SelectProgram";
import MoniesAvailablePostings from "./components/MoniesAvailablePostings";
import { isEmpty } from "@/shared/helpers";
import ModalWrapper from "./components/ModalWrapper";
import { getProgramBalance } from "@/services/program/getBalance";
import { setAuthProgram } from "@/containers/App/auth";
import store from "@/containers/App/store";
import { setStoreProgram } from "@/redux/actions/programActions";
import ViewInvoice from "./components/ViewInvoice";

const ManageAccount = ({ auth, program, organization }) => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [modalName, setModalName] = useState(null);

  const toggle = (name = null) => {
    if (name) {
      setModalName(name);
    }

    setOpen((prevState) => !prevState);
  };

  React.useEffect(() => {
    if (program && organization && typeof program.balance === "undefined") {
      getProgramBalance(organization.id, program.id).then((balance) => {
        program.balance = balance;
        setAuthProgram(program);
        store.dispatch(setStoreProgram(program));
      });
    }
  }, [organization, program]);

  const hasCardPayments = program?.invoice_for_awards !== 1;
  const readOnly = useIsReadOnlyManager();

  const billingActions = useMemo(() => {
    const actions = [
      {
        key: "CreateInvoice",
        eyebrow: "Funding",
        title: "Create Invoice",
        description: "Generate a single invoice for check or ACH funding.",
        primary: true,
      },
      {
        key: "MultipleInvoices",
        eyebrow: "Batch",
        title: "Bulk Invoices",
        description: "Prepare multiple invoice requests in one workflow.",
      },
      {
        key: "TransferMoney",
        eyebrow: "Transfers",
        title: "Transfer Funds",
        description: "Move money between programs without leaving billing.",
      },
    ];

    if (program?.is_pay_in_advance) {
      actions.splice(2, 0, {
        key: "InvoiceSummary",
        eyebrow: "Exports",
        title: "Invoice Summary",
        description: "Download the current invoice summary for reconciliation.",
      });
    }

    if (hasCardPayments) {
      actions.push({
        key: "Pay",
        eyebrow: "Payments",
        title: "Card Payment",
        description: "Submit a credit card payment directly into the program balance.",
      });
    }

    // Read Only Managers keep the Invoice Summary download only.
    return actions
      .filter((action) => !readOnly || action.key === "InvoiceSummary")
      .map((action) => ({
        ...action,
        primary: true,
      }));
  }, [hasCardPayments, program, readOnly]);

  if (!auth || !program || !organization) {
    return "Loading...";
  }

  return (
    <div className="manager-home manager-billing-page">
      <Container className="manager-home__shell manager-home__content">
        <div className="manager-billing">
          <section className="manager-card manager-billing__hero">
            <div className="manager-billing__hero-copy">
              <span className="manager-card__eyebrow">Billing</span>
              <h1 className="manager-billing__title">Program Billing</h1>
              <p className="manager-billing__description">
                Manage funding requests, invoice workflows, transfers, and account activity from one billing workspace.
              </p>
            </div>
          </section>

          <section className="manager-card manager-billing__toolbar">
            <div className="manager-billing__toolbar-grid">
              <div className="manager-billing__control">
                <span className="manager-billing__control-label">Program</span>
                <div className="manager-billing__control-body">
                  <SelectProgram showRefresh={false} label={null} variant="manager-report" />
                </div>
              </div>
              <div className="manager-billing__toolbar-actions">
                {invoiceId && (
                  <button
                    type="button"
                    className="manager-billing__secondary-btn"
                    onClick={() => navigate("/manager/manage-account")}
                  >
                    Back to Ledger
                  </button>
                )}
              </div>
            </div>

            <div className="manager-billing__actions">
              {billingActions.map((action) => (
                <button
                  type="button"
                  key={action.key}
                  className={`manager-billing__action-card${action.primary ? " is-primary" : ""}`}
                  onClick={() => toggle(action.key)}
                >
                  <span className="manager-billing__action-eyebrow">{action.eyebrow}</span>
                  <span className="manager-billing__action-title">{action.title}</span>
                  <span className="manager-billing__action-description">{action.description}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="manager-card manager-billing__panel">
            {!invoiceId && (
              <>
                <div className="manager-billing__panel-head">
                  <div>
                    <span className="manager-card__eyebrow">Ledger</span>
                    <h2 className="manager-card__title">Funds Ledger</h2>
                  </div>
                </div>
                <p className="manager-billing__panel-description">
                  Review funding activity, filter the date range, and reconcile deposits posted to this program.
                </p>
              </>
            )}

            {invoiceId ? (
              <div className="manager-billing__panel-body">
                <ViewInvoice
                  invoice={{ id: invoiceId }}
                  setStep={() => navigate("/manager/manage-account")}
                />
              </div>
            ) : (
              auth &&
              program &&
              !isEmpty(organization) && (
                <div className="manager-billing__panel-body">
                  <MoniesAvailablePostings
                    program={program}
                    organization={organization}
                  />
                </div>
              )
            )}
          </section>
        </div>
      </Container>

      <ModalWrapper
        name={modalName}
        isOpen={isOpen}
        toggle={toggle}
        setOpen={setOpen}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    program: state.program,
    organization: state.organization,
  };
};

export default connect(mapStateToProps)(ManageAccount);
